/**
 * API Helper Utilities
 * Common functions for working with API responses
 */
/**
 * Extract items array from API response
 * Handles different response formats:
 * - Direct array: [item1, item2]
 * - Wrapped in items: { items: [...] }
 * - Wrapped in data: { data: [...] }
 * - Custom keys: { customKey: [...] }
 *
 * @param response - API response data
 * @param altKeys - Alternative keys to check for array (e.g., ['customers', 'users'])
 * @returns Array of items
 */
export function extractItems(response, altKeys = []) {
    // Direct array
    if (Array.isArray(response))
        return response;
    // Standard envelope: { items: [...] }
    if (Array.isArray(response?.items))
        return response.items;
    // Check alternative keys
    for (const key of altKeys) {
        if (Array.isArray(response?.[key]))
            return response[key];
    }
    // Fallback: { data: [...] }
    if (Array.isArray(response?.data))
        return response.data;
    // No array found, return empty array
    return [];
}
/**
 * Build query string from parameters object
 * React Native compatible (doesn't use URLSearchParams)
 *
 * @param params - Object with query parameters
 * @returns Query string (without leading '?')
 */
export function buildQueryString(params) {
    const parts = [];
    for (const [key, value] of Object.entries(params)) {
        // Skip undefined, null, and empty string values
        if (value === undefined || value === null || value === '') {
            continue;
        }
        // Handle arrays
        if (Array.isArray(value)) {
            value.forEach((item) => {
                if (item !== undefined && item !== null && item !== '') {
                    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(item))}`);
                }
            });
        }
        else {
            parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
        }
    }
    return parts.join('&');
}
